import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Calendar, MapPin, Users, Clock } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * EventCard - Luxury event display card component
 * Displays event information with elegant styling and interactive elements
 * 
 * @param {Object} props
 * @param {string} props.title - Event title
 * @param {string} props.description - Event description
 * @param {string} props.date - Event date
 * @param {string} props.time - Event time
 * @param {string} props.location - Event location
 * @param {number} props.attendees - Number of attendees
 * @param {number} props.maxAttendees - Maximum number of attendees
 * @param {string} props.imageUrl - Event image URL
 * @param {string} props.category - Event category
 * @param {string} props.price - Event price
 * @param {boolean} props.featured - Whether event is featured
 * @param {Function} props.onViewDetails - Callback when view details is clicked
 * @param {Function} props.onJoin - Callback when join button is clicked
 * @param {string} props.className - Additional CSS classes
 */
import EventRatingBadge from '@/components/EventRatingBadge';

const EventCard = ({
  id,
  title,
  description,
  date,
  time,
  location,
  attendees = 0,
  maxAttendees,
  imageUrl,
  category,
  price,
  featured = false,
  onViewDetails,
  onJoin,
  className,
  ...props
}) => {
  const spotsLeft = maxAttendees ? maxAttendees - attendees : null;
  const isAlmostFull = spotsLeft && spotsLeft <= 5;

  return (
    <Card 
      className={cn(
        "overflow-hidden transition-all duration-300 hover:shadow-gold group cursor-pointer",
        featured && "ring-2 ring-secondary-500",
        className
      )}
      {...props}
    >
      {/* Event Image */}
      {imageUrl && (
        <div className="relative h-48 overflow-hidden">
          <img 
            src={imageUrl} 
            alt={title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          {featured && (
            <Badge className="absolute top-3 right-3 bg-secondary-500 text-secondary-foreground shadow-gold">
              Featured
            </Badge>
          )}
          {category && (
            <Badge className="absolute top-3 left-3 bg-primary-500 text-primary-foreground">
              {category}
            </Badge>
          )}
        </div>
      )}

      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-display text-xl font-semibold text-primary-700 group-hover:text-primary-500 transition-colors">
            {title}
          </CardTitle>
          {price && (
            <span className="text-lg font-bold text-secondary-600 whitespace-nowrap">
              {price}
            </span>
          )}
        </div>
        {description && (
          <CardDescription className="text-body line-clamp-2">
            {description}
          </CardDescription>
        )}
        {/* Bundle 77 - role-aware rating badge; hidden below threshold. */}
        {id ? <div className="pt-1"><EventRatingBadge eventId={id} /></div> : null}
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Date & Time */}
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <Calendar className="w-4 h-4 text-primary-500" />
          <span>{date}</span>
          {time && (
            <>
              <Clock className="w-4 h-4 text-primary-500 ml-2" />
              <span>{time}</span>
            </>
          )}
        </div>

        {/* Location */}
        {location && (
          <div className="flex items-center gap-2 text-sm text-neutral-600">
            <MapPin className="w-4 h-4 text-primary-500" />
            <span className="line-clamp-1">{location}</span>
          </div>
        )}

        {/* Attendees */}
        {maxAttendees && (
          <div className="flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 text-primary-500" />
            <span className="text-neutral-600">
              {attendees} / {maxAttendees} attending
            </span>
            {isAlmostFull && (
              <Badge variant="destructive" className="ml-auto text-xs">
                {spotsLeft} spots left
              </Badge>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex gap-2">
        {onViewDetails && (
          <Button 
            variant="outline" 
            className="flex-1 border-primary-500 text-primary-500 hover:bg-primary-50"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails();
            }}
          >
            View Details
          </Button>
        )}
        {onJoin && (
          <Button 
            className="flex-1 bg-secondary-500 hover:bg-secondary-600 text-secondary-foreground shadow-gold"
            onClick={(e) => {
              e.stopPropagation();
              onJoin();
            }}
            disabled={spotsLeft === 0}
          >
            {spotsLeft === 0 ? 'Full' : 'Join Event'}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default EventCard;